import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Button from "../../components/form-controls/Button";
import Input from "../../components/form-controls/Input";
import Select from 'react-select'
import { UsersService } from "../../services/UsersService";
import { useSession } from "../../hooks/useSession";
import { PROFILES } from "../../constants";
import InvalidFeedback from "../../components/InvalidFeedback";
import { AlertService } from "../../services/Alert";
import { useTranslation } from "react-i18next";
declare const bootstrap: any;

interface ModalUserProps {
    user: any;
    show: boolean;
    onRequestClose: () => void
    reload: () => void
}
function ModalUser({ user, show, onRequestClose, reload }: ModalUserProps) {

    const { t } = useTranslation();

    const emailRegex = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

    const { session } = useSession();
    const modalRef = useRef<HTMLDivElement>(null)
    const [modal, setModal] = useState<any>(null)
    const [profile, setProfile] = useState<any>([]);
    const [accounts, setAccounts] = useState<any>([]);

    const defaultValues = {
        user_id: '',
        username: '',
        email: '',
        // sendToMyEmail: false,
        complete_name: '',
        profile_id: '',
        account_id: '',
    }

    const { register, handleSubmit, watch, setValue, setError, clearErrors, reset, control, formState: { errors, isValid, isSubmitting } } = useForm({
        mode: 'onChange',
        defaultValues
    });
    
    const getAccountsIds = user && user.accounts? user.accounts.map(a => a.account_id).join(','): '';
    

    const getProfiles = async () => {
        try {
            const { data } = await UsersService.getProfiles();
            setProfile(data);
        } catch (error) {
            console.log(error);
        }
    }

    const getAccounts = async() => {
        try {
            const { data } = await UsersService.getAccount(session.id);
            setAccounts(data.map( p => ({value: p.id, label: p.nombre })));
        } catch (error) {
            console.log(error);
        }
    }

    const saveOrUpdateUser = async(method: 'editUser' | 'saveUser', data) => {
        try {
            await UsersService[method](data);

            AlertService.success('Éxito!', 'Se guardó correctame al usuario')

            reset(defaultValues)
            onRequestClose();
            reload()
        } catch (error) {
            AlertService.error('¡Error!', error)
        }
    }

    const submit = async (data) => {
        const method = data.user_id ? 'editUser': 'saveUser';
        await saveOrUpdateUser(method, data)
    }

    const handleShowHideModal = () => {
        if(modal) {
            show ? modal.show(): modal.hide()
        }
    }

    const handleDataUser = () => {
        if(user){
            reset({
                user_id: user.id,
                username: user.username,
                email: user.email,
                complete_name: user.fullname,
                profile_id: user.type,
                account_id: getAccountsIds
            });
        } else {
            reset(defaultValues);
        }
    }
        
    const closeAndReset = () => {
        onRequestClose(); 
    }

    const username = watch('username')
    const profileIdSelected = watch('profile_id');

    const profiles = useMemo(() => profile?.filter(p => p.type.toLowerCase()!== PROFILES.INICIADOR).map( p => ({value: p.id, label: p.type})), [profile])

    const isMultiSelect = useMemo(() => {
        const profile = profiles?.find( p => +p.value === +profileIdSelected );

        if(!profile) return false;

        return profile.label.toLowerCase() === PROFILES.ADMIN || profile.label.toLowerCase() === PROFILES.SUPERVISOR

    }, [profileIdSelected]);


    const existUserId = watch('user_id');

    const isValidUsername = async() => {
        try {
            const { data: { valid }} = await UsersService.isUsernameAvailable({
                username: username,
                userId: user ? user.id: 0
            })

            if(!valid) {
                setError('username', { type: 'custom', message: `El usuario ${username} no esta disponible` })
            } else {
                clearErrors('username')
            }

        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        if(!show) return;
        isValidUsername()
    }, [username]);

    
    useEffect(() => {
        if(!user) setValue('account_id', '')
    }, [profileIdSelected]);

    useEffect(() => {
        if (modalRef.current) {
            const modalInstance = new bootstrap.Modal(modalRef.current, {})
            setModal(modalInstance)
        }
    }, [modalRef])


    useEffect(() => {
        handleShowHideModal()

        if( show ) {
            handleDataUser();
        }
        
    }, [show, user]);

    useEffect(() => {
        getAccounts();
        getProfiles();
    }, []);

    return (
        <div className="modal fade" ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog" role="document">
                <form onSubmit={handleSubmit(submit)}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title" >
                                { existUserId ? t("platform.users.edit-user") : t("platform.users.add-user") }
                            </h4>
                            <button type="button" className="btn-close" onClick={closeAndReset}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row ">
                                <div className="mb-3">
                                    <Input label={t("platform.users.username-form") } errors={errors.username} {...register('username', { required: true, setValueAs: (value) => value.trim() })} />
                                </div>
                                <div className="mb-3">
                                    <Input label={t("platform.users.email-form") } errors={errors.email} {...register('email', { required: true, pattern: { value: emailRegex, message: 'Debes ingresar un correo válido' } })} />
                                </div>
                                <div className="mb-3">
                                    <Input label={t("platform.users.full-name") } errors={errors.complete_name} {...register('complete_name', { required: true })} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t("platform.users.profile-form") }</label>
                                    <Controller
                                        control={control}
                                        name='profile_id'
                                        rules={{ required: true }}
                                        render={
                                            ({
                                                field: { onChange, value },
                                                fieldState: { error }
                                            }) => {
                                                const defaultValue = profiles?.find( p => +p.value === +value);

                                                return (
                                                    <>
                                                        <Select
                                                            options={profiles}
                                                            isMulti={false}
                                                            className={'form-select-custom ' + (error ? ' is-invalid' : '')}
                                                            classNamePrefix='form-select-custom'
                                                            placeholder='Perfil'
                                                            onChange={ (data: any) => onChange(data.value) }
                                                            value={defaultValue}
                                                            isDisabled={ user ? true: false}
                                                        />
                                                         <InvalidFeedback error={error} />
                                                    </>
                                                )
                                            }
                                        }
                                    />
                                    
                                </div>

                                <div className='mb-3'>
                                    <label className="form-label">{t("platform.users.account-form") }</label>
                                    <Controller
                                        control={control}
                                        name='account_id'
                                        rules={{ required: true }}
                                        render={
                                            ({
                                                field: { onChange, value },
                                                fieldState: { error }
                                            }) => {
                                                let defaultValue = {};
                                                if(isMultiSelect) {
                                                    const ids = value.toString().split(',').map( v => +v);
                                                    defaultValue = accounts?.filter( a => ids.indexOf(+a.value) !== -1 )
                                                } else {
                                                    defaultValue = accounts?.find( p => p.value === +value) || '';
                                                }                                                

                                                return (
                                                    <>
                                                        <Select
                                                            options={accounts}
                                                            isMulti={isMultiSelect}
                                                            className={'form-select-custom ' + (error ? ' is-invalid' : '')}
                                                            classNamePrefix='form-select-custom'
                                                            placeholder='Cuenta'
                                                            onChange={ (data: any) => {
                                                                if(Array.isArray(data)) {
                                                                    onChange(data.map( d => d.value).join(','))
                                                                } else {
                                                                    onChange(data.value) 
                                                                }
                                                                
                                                            } }
                                                            value={defaultValue}
                                                        />
                                                         <InvalidFeedback error={error} />
                                                    </>
                                                )
                                            }
                                        
                                        }
                                    />

                                </div>

                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-white btn-sm" onClick={closeAndReset}>{t("btn-cancel") }</button>
                            <Button type="submit" className="btn btn-primary btn-sm" disabled={!isValid} isLoading={isSubmitting}>
                                { existUserId ? t("platform.users.edit") : t("btn-save") }
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
export default ModalUser